import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  SalaryAdvanceAvailability,
  SalaryAdvanceEligibility,
  SalaryAdvanceEmployeeCategory,
  SalaryAdvancePolicy,
  SalaryAdvanceRequest,
  SalaryAdvanceStatus,
} from "@/types/domain";

export interface SalaryAdvanceFilters extends PageParams {
  status?: SalaryAdvanceStatus | "";
}

export interface SalaryAdvanceInput {
  requested_amount: number;
  reason: string;
  required_date: string;
  supporting_document?: string;
}

export interface SalaryAdvancePolicyInput {
  enabled?: boolean;
  max_amount?: number;
  max_percent_of_monthly_salary?: number;
  min_tenure_months?: number;
  max_pending_requests?: number;
  allowed_categories?: SalaryAdvanceEmployeeCategory[];
  payroll_cutoff_block_days?: number;
  max_repayment_cycles?: number;
}

export const salaryAdvanceApi = {
  list: (filters: SalaryAdvanceFilters = {}) => http.getPage<SalaryAdvanceRequest>("/salary-advances", filters),
  get: (id: string) => http.get<SalaryAdvanceRequest>(`/salary-advances/${id}`),

  // The eligibility engine pre-check - "Employee clicks Salary Advance
  // Request -> Eligibility Engine -> Eligible?" Always the caller's own
  // employee record.
  checkEligibility: () => http.get<SalaryAdvanceEligibility>("/salary-advances/eligibility-check"),

  // Employee's budget summary - Available Advance Amount / Maximum Request
  // Limit / Existing Pending Advance. Never returns salary details.
  availableAmount: () => http.get<SalaryAdvanceAvailability>("/salary-advances/available-amount"),
  create: (input: SalaryAdvanceInput) => http.post<SalaryAdvanceRequest>("/salary-advances", input),
  update: (id: string, input: Partial<SalaryAdvanceInput>) => http.put<SalaryAdvanceRequest>(`/salary-advances/${id}`, input),
  submit: (id: string) => http.post<SalaryAdvanceRequest>(`/salary-advances/${id}/submit`, {}),

  // Reporting Manager's stage: approve (recommend), reject, or return for correction.
  managerDecide: (id: string, decision: "approve" | "reject" | "return", comments?: string) =>
    http.put<SalaryAdvanceRequest>(`/salary-advances/${id}/manager-decision`, { decision, comments }),

  // HR Admin's verification stage.
  hrDecide: (id: string, approve: boolean, comments?: string) =>
    http.put<SalaryAdvanceRequest>(`/salary-advances/${id}/hr-decision`, { approve, comments }),

  // Payroll Admin's final processing stage.
  process: (id: string, referenceNumber?: string, notes?: string) =>
    http.put<SalaryAdvanceRequest>(`/salary-advances/${id}/process`, { reference_number: referenceNumber, notes }),

  // Tenant Admin's policy configuration.
  policy: () => http.get<SalaryAdvancePolicy>("/settings/modules/salary-advance-request"),
  updatePolicy: (input: SalaryAdvancePolicyInput) => http.put<SalaryAdvancePolicy>("/settings/modules/salary-advance-request", input),
};
